import React, { useState, useEffect, useRef, useCallback } from "react";
import * as axios from "axios";
import { toastError } from "../../shared/toastHelper";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";

const formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const money = (cents) => formatter.format(cents / 100);

export const useStagedBreakdown = ({ annualEnabled, monthlyEnabled }) => {
  const { csrfToken, team, membershipProduct, isEditing } = useMembershipProductNewContext();
  const [staged, setStaged] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef(null);
  const prevValuesRef = useRef(null);
  const latestRequestRef = useRef(0);

  const absorbFees = membershipProduct.absorb_fees !== false;
  const showTicketing = membershipProduct.add_to_event_ticketing;
  const amount = (record) => (record ? record.amount_cents : undefined);
  const defaultAnnual = amount(membershipProduct.default_price);
  const ticketingAnnual = amount(membershipProduct.ticketing_price_record);
  const defaultMonthly = amount(membershipProduct.default_monthly_price);
  const ticketingMonthly = amount(membershipProduct.ticketing_monthly_price_record);
  const billingVenueId = membershipProduct.billing_venue_ownership_id;

  const fetchStaged = useCallback(async () => {
    const request = (latestRequestRef.current += 1);
    const isLatest = () => request === latestRequestRef.current;
    if (!billingVenueId || (!(annualEnabled && defaultAnnual) && !(monthlyEnabled && defaultMonthly))) {
      setStaged(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const url = isEditing
        ? `/teams/${team.id}/membership_products/${membershipProduct.id}/stage.json`
        : `/teams/${team.id}/membership_products/stage.json`;
      const response = await axios.post(url, {
        membership_product: {
          billing_venue_ownership_id: billingVenueId,
          absorb_fees: absorbFees,
          default_annual_amount_cents: annualEnabled && defaultAnnual ? defaultAnnual : null,
          ticketing_annual_amount_cents: annualEnabled && showTicketing && ticketingAnnual ? ticketingAnnual : null,
          default_monthly_amount_cents: monthlyEnabled && defaultMonthly ? defaultMonthly : null,
          ticketing_monthly_amount_cents: monthlyEnabled && showTicketing && ticketingMonthly ? ticketingMonthly : null,
        },
      });
      if (isLatest()) setStaged(response.data);
    } catch (error) {
      if (!isLatest()) return;
      toastError("Failed to calculate price breakdown");
      setStaged(null);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [
    csrfToken,
    team.id,
    membershipProduct.id,
    isEditing,
    billingVenueId,
    absorbFees,
    showTicketing,
    annualEnabled,
    monthlyEnabled,
    defaultAnnual,
    ticketingAnnual,
    defaultMonthly,
    ticketingMonthly,
  ]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const current = { billingVenueId, absorbFees, showTicketing, annualEnabled, monthlyEnabled };
    const prev = prevValuesRef.current;
    prevValuesRef.current = current;
    const settingChanged = !prev || Object.keys(current).some((key) => prev[key] !== current[key]);
    if (settingChanged) fetchStaged();
    else debounceTimerRef.current = setTimeout(fetchStaged, 500);
    return () => debounceTimerRef.current && clearTimeout(debounceTimerRef.current);
  }, [fetchStaged]);

  return { staged, loading };
};

const breakdownFor = (staged, slot, amountCents, totalFeePercentage, absorbFees) => {
  if (staged && staged[slot]) return staged[slot];
  if (!amountCents || amountCents <= 0) return null;
  const fee = Math.round((amountCents * totalFeePercentage) / 100);
  return {
    amount_cents: amountCents,
    total_fee_amount_cents: fee,
    total_tax_amount_cents: 0,
    customer_pays_cents: absorbFees ? amountCents : amountCents + fee,
    net_to_venue_cents: absorbFees ? amountCents - fee : amountCents,
  };
};

const PriceBreakdownTable = ({ cadence, staged, loading }) => {
  const { membershipProduct, totalFeePercentage } = useMembershipProductNewContext();
  const absorbFees = membershipProduct.absorb_fees !== false;
  const records =
    cadence === "annual"
      ? [membershipProduct.default_price, membershipProduct.ticketing_price_record]
      : [membershipProduct.default_monthly_price, membershipProduct.ticketing_monthly_price_record];
  const amount = (record) => (record ? record.amount_cents : undefined);

  const columns = [{ name: "Default", value: breakdownFor(staged, `default_${cadence}`, amount(records[0]), totalFeePercentage, absorbFees) }];
  if (membershipProduct.add_to_event_ticketing) {
    columns.push({ name: "Ticketing", value: breakdownFor(staged, `ticketing_${cadence}`, amount(records[1]), totalFeePercentage, absorbFees) });
  }
  if (!columns[0].value) return null;

  const cell = (b, fn) => (b ? fn(b) : "—");
  const hasTax = columns.some((c) => c.value && c.value.total_tax_amount_cents > 0);
  const lines = [
    { key: "base", line: "Base price", fn: (b) => money(b.amount_cents) },
    {
      key: "fees",
      line: `Fees (${totalFeePercentage}%)`,
      fn: (b) => (absorbFees ? `(${money(b.total_fee_amount_cents)})` : money(b.total_fee_amount_cents)),
    },
    hasTax && { key: "tax", line: "Sales tax", fn: (b) => money(b.total_tax_amount_cents || 0) },
    { key: "pays", line: "Customer pays", fn: (b) => money(b.customer_pays_cents), strong: true },
    { key: "net", line: "Net to venue", fn: (b) => money(b.net_to_venue_cents), strong: true },
  ].filter(Boolean);

  return (
    <div className={`membership-breakdown${loading ? " is-loading" : ""}`} data-testid={`membership-breakdown-${cadence}`}>
      <table className="membership-breakdown-table">
        {columns.length > 1 ? (
          <thead>
            <tr>
              <th>
                <span className="sr-only">Line</span>
              </th>
              {columns.map((c) => (
                <th key={c.name}>{c.name}</th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {lines.map((row) => (
            <tr key={row.key} className={row.strong ? "is-total" : undefined}>
              <td>{row.line}</td>
              {columns.map((c) => (
                <td key={c.name}>{cell(c.value, row.fn)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PriceBreakdownTable;
